/**
 * Alteia Scroll Animations
 *
 * Per-element viewport-driven entrance animations using Intersection Observer.
 * Each element is observed individually — stagger applies only within the same
 * observer callback batch (row-aware), matching homepage reference sections.
 *
 * Attributes used on HTML elements:
 *   data-animate="fade-up"         Animation type (set by PHP)
 *   data-animate-delay="0.1"       Optional per-element fixed delay
 *   data-animate-order="3"         Sequence order for grouped card animations
 *   data-animate-sequence          Group trigger (workflow-timeline, performance-badges)
 *   data-animate-hero              Hero banner: auto-plays on page load
 *   data-animate-min-visible="300" Reveal once N px of the element is in view
 *                                  (for tall targets where % of height is unreliable)
 */

( () => {
	'use strict';

	const config = window.alteiaAnimConfig || {};

	if ( ! config.enabled ) {
		return;
	}

	/**
	 * Containers whose descendants must never run entrance animations.
	 * Modal open/close motion stays with each component's own JS.
	 */
	const EXCLUDE_ANCESTOR_SELECTOR = [
		'dialog',
		'[role="dialog"]',
		'.alteia-mtm-modal',
		'.alteia-tfcs-modal',
		'.alteia-leadership-section__modal',
		'.alteia-disclaimer-modal',
		'.alteia-header__drawer',
		'.alteia-header__drawer-backdrop',
	].join( ', ' );

	// Respect prefers-reduced-motion.
	if (
		config.reducedMotion &&
		window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
	) {
		revealAll();
		return;
	}

	// Disable on mobile if turned off.
	if ( ! config.mobileEnabled && window.innerWidth < 782 ) {
		revealAll();
		return;
	}

	// Set CSS custom properties from admin config.
	const root = document.documentElement;
	root.style.setProperty( '--alteia-anim-duration', config.duration + 's' );
	root.style.setProperty( '--alteia-anim-easing', config.easing );
	root.style.setProperty(
		'--alteia-anim-distance-base',
		config.distance + 'px'
	);
	root.style.setProperty( '--alteia-anim-distance', config.distance + 'px' );

	const globalDelay = Math.max( 0, parseFloat( config.delay ) || 0 );
	const stagger = Math.max( 0, parseFloat( config.stagger ) || 0 );
	const threshold = Math.min(
		Math.max( parseFloat( config.threshold ) || 0.15, 0 ),
		1
	);
	const durationMs = Math.max(
		0,
		( parseFloat( config.duration ) || 0.7 ) * 1000
	);

	/**
	 * Whether an element should be ignored by the entrance system.
	 *
	 * @param {Element} el Candidate element.
	 * @return {boolean}
	 */
	function isExcluded( el ) {
		if ( ! el || ! el.closest ) {
			return true;
		}

		return Boolean( el.closest( EXCLUDE_ANCESTOR_SELECTOR ) );
	}

	const WORKFLOW_SEQUENCE = 'workflow-timeline';
	const PERFORMANCE_BADGES_SEQUENCE = 'performance-badges';
	const SEQUENCE_SELECTOR =
		'[data-animate-sequence="workflow-timeline"], [data-animate-sequence="performance-badges"]';
	const WORKFLOW_CARD_CLASS = 'alteia-workflow-timeline-section__card';
	const PERFORMANCE_BADGE_CLASS =
		'alteia-performance-overview-section__badge';
	const playedSequenceRoots = new WeakSet();

	/**
	 * Ordered stagger for a grouped sequence (seconds between items).
	 *
	 * @param {string} sequence Sequence type.
	 * @return {number}
	 */
	function sequenceOrderedStagger( sequence ) {
		if ( sequence === PERFORMANCE_BADGES_SEQUENCE ) {
			return 0.4;
		}

		return 0.5;
	}

	/**
	 * Whether the element is played by a grouped sequence, not the
	 * per-element observer (workflow cards, performance badges).
	 *
	 * @param {Element} el Candidate element.
	 * @return {boolean}
	 */
	function isGroupedSequenceItem( el ) {
		return Boolean(
			el &&
				el.classList &&
				( el.classList.contains( WORKFLOW_CARD_CLASS ) ||
					el.classList.contains( PERFORMANCE_BADGE_CLASS ) )
		);
	}

	/**
	 * Decide whether an intersecting entry should play its entrance.
	 *
	 * Tall elements can never reach a high intersectionRatio (max ratio is
	 * viewport/element height). For those, trigger once a homepage-equivalent
	 * slice of the element has entered the viewport.
	 *
	 * Elements with data-animate-min-visible use a fixed pixel trigger instead
	 * of a percentage of their full height (e.g. long legal body copy).
	 *
	 * @param {IntersectionObserverEntry} entry Observer entry.
	 * @return {boolean}
	 */
	function shouldReveal( entry ) {
		if ( ! entry.isIntersecting ) {
			return false;
		}

		const minVisible = parseFloat(
			entry.target.getAttribute( 'data-animate-min-visible' ) || ''
		);

		if ( minVisible > 0 ) {
			const visibleHeight = entry.intersectionRect
				? entry.intersectionRect.height
				: 0;
			const targetHeight = entry.boundingClientRect.height || 0;

			// Reveal once ~N px has entered, or the full element if shorter.
			return visibleHeight >= Math.min( minVisible, targetHeight );
		}

		if ( entry.intersectionRatio >= threshold ) {
			return true;
		}

		// Tall-element fallback: element is taller than the viewport can cover
		// at the configured threshold, so ratio can never reach it.
		const rootHeight =
			( entry.rootBounds && entry.rootBounds.height ) ||
			window.innerHeight ||
			0;
		const targetHeight = entry.boundingClientRect.height;

		if ( rootHeight <= 0 || targetHeight <= 0 ) {
			return false;
		}

		const maxPossibleRatio = rootHeight / targetHeight;
		if ( maxPossibleRatio >= threshold ) {
			return false;
		}

		// Require at least `threshold` of the viewport to be covered by the
		// element (same visual "amount on screen" as a small card at threshold).
		const visibleHeight = entry.intersectionRect
			? entry.intersectionRect.height
			: 0;

		return visibleHeight >= rootHeight * threshold;
	}

	/**
	 * Sort by visual row, then left-to-right (default stagger order).
	 *
	 * @param {Element} a First element.
	 * @param {Element} b Second element.
	 * @return {number} Sort comparison.
	 */
	function sortByVisualPosition( a, b ) {
		const rectA = a.getBoundingClientRect();
		const rectB = b.getBoundingClientRect();
		const rowDiff = rectA.top - rectB.top;
		if ( Math.abs( rowDiff ) > 50 ) {
			return rowDiff;
		}
		return rectA.left - rectB.left;
	}

	/**
	 * Reorder elements that declare data-animate-order among their visual slots.
	 *
	 * Other elements keep their visual stagger position. Used by Workflow
	 * Timeline cards so sequence follows timeline number, not row direction.
	 *
	 * @param {Element[]} elements Visually sorted elements.
	 */
	function applyAnimateOrder( elements ) {
		const orderedIndexes = [];

		elements.forEach( ( el, i ) => {
			if ( el.hasAttribute( 'data-animate-order' ) ) {
				orderedIndexes.push( i );
			}
		} );

		if ( orderedIndexes.length < 2 ) {
			return;
		}

		const orderedEls = orderedIndexes.map( ( i ) => elements[ i ] );
		orderedEls.sort( ( a, b ) => {
			return (
				( parseFloat( a.getAttribute( 'data-animate-order' ) ) || 0 ) -
				( parseFloat( b.getAttribute( 'data-animate-order' ) ) || 0 )
			);
		} );

		orderedIndexes.forEach( ( slot, i ) => {
			elements[ slot ] = orderedEls[ i ];
		} );
	}

	/**
	 * Sort and play entrances for a batch of newly-visible elements.
	 *
	 * @param {Element[]} elements             Elements to animate.
	 * @param {number}    [orderedStagger=0.4] Delay between ordered sequence items.
	 */
	function animateBatch( elements, orderedStagger ) {
		if ( ! elements.length ) {
			return;
		}

		elements.sort( sortByVisualPosition );
		applyAnimateOrder( elements );

		const orderedCardStagger =
			typeof orderedStagger === 'number' ? orderedStagger : 0.4;
		const firstOrderedIndex = elements.findIndex( ( el ) =>
			el.hasAttribute( 'data-animate-order' )
		);

		elements.forEach( ( el, index ) => {
			const elementDelay =
				parseFloat( el.getAttribute( 'data-animate-delay' ) ) || 0;
			let staggerDelay = stagger * index;

			if ( el.hasAttribute( 'data-animate-order' ) ) {
				const order =
					parseFloat( el.getAttribute( 'data-animate-order' ) ) || 1;
				const base =
					firstOrderedIndex > 0 ? stagger * firstOrderedIndex : 0;
				staggerDelay = base + orderedCardStagger * Math.max( 0, order - 1 );
			}

			const itemDelay = globalDelay + elementDelay + staggerDelay;
			el.style.setProperty(
				'--alteia-anim-delay',
				itemDelay.toFixed( 3 ) + 's'
			);
			el.classList.add( 'is-animated' );
			el.addEventListener( 'transitionend', handleTransitionEnd, {
				once: true,
			} );

			// Fallback if transitionend never fires (interrupted CSS, duration 0).
			const failsafe = window.setTimeout(
				() => {
					el.classList.add( 'anim-done' );
				},
				durationMs + itemDelay * 1000 + 100
			);

			el.addEventListener(
				'transitionend',
				() => {
					window.clearTimeout( failsafe );
				},
				{ once: true }
			);
		} );
	}

	/**
	 * Intersection Observer: triggers per element once enough of it is visible.
	 * Includes 0 so exit / re-entry can be detected when Animate Once is off.
	 */
	const observer = new IntersectionObserver(
		( entries ) => {
			const visible = [];

			entries.forEach( ( entry ) => {
				if (
					isExcluded( entry.target ) ||
					isGroupedSequenceItem( entry.target )
				) {
					observer.unobserve( entry.target );
					return;
				}

				if ( shouldReveal( entry ) ) {
					if ( ! entry.target.classList.contains( 'is-animated' ) ) {
						visible.push( entry.target );
					}

					if ( config.once ) {
						observer.unobserve( entry.target );
					}
					return;
				}

				// Below threshold (or fully out of view): reset for re-animation.
				if (
					! config.once &&
					entry.target.classList.contains( 'is-animated' )
				) {
					entry.target.classList.remove( 'is-animated', 'anim-done' );
					entry.target.style.removeProperty( '--alteia-anim-delay' );
				}
			} );

			animateBatch( visible );
		},
		{
			rootMargin: '0px',
			threshold: buildThresholdList( threshold ),
		}
	);

	/**
	 * Play grouped sequence items in declared order as a single sequence.
	 *
	 * @param {Element} root Container marked with data-animate-sequence.
	 */
	function playGroupedSequence( root ) {
		if ( ! root || playedSequenceRoots.has( root ) ) {
			return;
		}

		const items = Array.from(
			root.querySelectorAll( '[data-animate][data-animate-order]' )
		).filter( ( el ) => ! isExcluded( el ) );

		if ( ! items.length ) {
			return;
		}

		playedSequenceRoots.add( root );
		animateBatch(
			items,
			sequenceOrderedStagger( root.getAttribute( 'data-animate-sequence' ) )
		);
	}

	/**
	 * Start the workflow sequence once the card grid has entered view.
	 *
	 * Tall 9-card layouts may never reach a high intersectionRatio, so
	 * also start when the top of the grid is on screen.
	 *
	 * @param {IntersectionObserverEntry} entry Observer entry.
	 * @return {boolean}
	 */
	function shouldRevealWorkflowSequence( entry ) {
		if ( ! entry.isIntersecting ) {
			return false;
		}

		if ( shouldReveal( entry ) ) {
			return true;
		}

		const rect = entry.boundingClientRect;
		const rootHeight =
			( entry.rootBounds && entry.rootBounds.height ) ||
			window.innerHeight ||
			0;

		if ( rootHeight <= 0 ) {
			return false;
		}

		return rect.top < rootHeight * ( 1 - Math.min( threshold, 0.15 ) );
	}

	const workflowSequenceObserver = new IntersectionObserver(
		( entries ) => {
			entries.forEach( ( entry ) => {
				if ( shouldRevealWorkflowSequence( entry ) ) {
					playGroupedSequence( entry.target );
					workflowSequenceObserver.unobserve( entry.target );
				}
			} );
		},
		{
			rootMargin: '0px',
			threshold: buildThresholdList( threshold ),
		}
	);

	/**
	 * Observe a workflow cards grid for the dedicated sequence.
	 *
	 * @param {Element} root Sequence root.
	 */
	function observeWorkflowSequenceRoot( root ) {
		if ( ! root || ! root.getAttribute ) {
			return;
		}

		const sequence = root.getAttribute( 'data-animate-sequence' );

		if (
			sequence !== WORKFLOW_SEQUENCE &&
			sequence !== PERFORMANCE_BADGES_SEQUENCE
		) {
			return;
		}

		if ( playedSequenceRoots.has( root ) ) {
			return;
		}

		workflowSequenceObserver.observe( root );
	}

	/**
	 * Observer thresholds: dense list so tall-element ratio changes are noticed.
	 *
	 * @param {number} value Configured viewport threshold (0–1).
	 * @return {number[]}
	 */
	function buildThresholdList( value ) {
		const list = [ 0, value, 1 ];
		// Extra steps help tall elements report ratio changes before max ratio.
		for ( let i = 0.05; i < 1; i += 0.05 ) {
			list.push( Math.round( i * 100 ) / 100 );
		}
		return list.filter(
			( item, index, arr ) => arr.indexOf( item ) === index
		);
	}

	/**
	 * Free GPU memory after animation completes.
	 * @param {TransitionEvent} e
	 */
	function handleTransitionEnd( e ) {
		if ( e.propertyName === 'opacity' || e.propertyName === 'transform' ) {
			e.target.classList.add( 'anim-done' );
		}
	}

	/**
	 * Reveal all animated elements immediately (no-animation fallback).
	 */
	function revealAll() {
		document.querySelectorAll( '[data-animate]' ).forEach( ( el ) => {
			if ( isExcluded( el ) ) {
				return;
			}
			el.classList.add( 'is-animated', 'anim-done' );
		} );
	}

	/**
	 * Resolve final delay for an element (global base + per-element).
	 *
	 * @param {Element} el Animated element.
	 * @return {string} Delay in seconds as a string.
	 */
	function resolveDelay( el ) {
		const elementDelay =
			parseFloat( el.getAttribute( 'data-animate-delay' ) ) || 0;
		return ( globalDelay + elementDelay ).toFixed( 3 );
	}

	/**
	 * Whether an element currently has a rendered layout box.
	 *
	 * @param {Element} el Element to test.
	 * @return {boolean}
	 */
	function hasLayoutBox( el ) {
		if ( ! el.getClientRects || el.getClientRects().length === 0 ) {
			return false;
		}

		const style = window.getComputedStyle( el );
		return style.display !== 'none' && style.visibility !== 'hidden';
	}

	/**
	 * Manually evaluate pending targets after layout changes (resize / pageshow).
	 *
	 * Uses the same reveal + stagger path as the observer so behaviour stays
	 * consistent with homepage sections (no instant pop-in).
	 */
	function syncPendingTargets() {
		const pending = [];

		document
			.querySelectorAll( '[data-animate]:not(.is-animated)' )
			.forEach( ( el ) => {
				if ( isExcluded( el ) ) {
					return;
				}

				if ( isGroupedSequenceItem( el ) ) {
					return;
				}

				if ( el.closest( '[data-animate-hero]' ) ) {
					return;
				}

				if ( ! hasLayoutBox( el ) ) {
					return;
				}

				// Keep observing so future scroll still works.
				observer.observe( el );

				const rect = el.getBoundingClientRect();
				const rootHeight = window.innerHeight || 0;
				const visibleTop = Math.max( rect.top, 0 );
				const visibleBottom = Math.min( rect.bottom, rootHeight );
				const visibleHeight = Math.max( 0, visibleBottom - visibleTop );
				const isIntersecting =
					visibleHeight > 0 &&
					rect.right > 0 &&
					rect.left < ( window.innerWidth || 0 );
				const ratio = rect.height > 0 ? visibleHeight / rect.height : 0;

				const fakeEntry = {
					isIntersecting,
					intersectionRatio: ratio,
					boundingClientRect: rect,
					intersectionRect: { height: visibleHeight },
					rootBounds: { height: rootHeight },
					target: el,
				};

				if ( shouldReveal( fakeEntry ) ) {
					pending.push( el );
					if ( config.once ) {
						observer.unobserve( el );
					}
				}
			} );

		animateBatch( pending );

		document
			.querySelectorAll( SEQUENCE_SELECTOR )
			.forEach( ( root ) => {
				if ( playedSequenceRoots.has( root ) ) {
					return;
				}

				if ( ! hasLayoutBox( root ) ) {
					observeWorkflowSequenceRoot( root );
					return;
				}

				const rect = root.getBoundingClientRect();
				const rootHeight = window.innerHeight || 0;
				const visibleTop = Math.max( rect.top, 0 );
				const visibleBottom = Math.min( rect.bottom, rootHeight );
				const visibleHeight = Math.max( 0, visibleBottom - visibleTop );
				const fakeEntry = {
					isIntersecting: visibleHeight > 0,
					intersectionRatio:
						rect.height > 0 ? visibleHeight / rect.height : 0,
					boundingClientRect: rect,
					intersectionRect: { height: visibleHeight },
					rootBounds: { height: rootHeight },
					target: root,
				};

				if ( shouldRevealWorkflowSequence( fakeEntry ) ) {
					playGroupedSequence( root );
					workflowSequenceObserver.unobserve( root );
				} else {
					observeWorkflowSequenceRoot( root );
				}
			} );
	}

	/**
	 * Initialize: observe all [data-animate] elements individually.
	 */
	function init() {
		// Hero banners: animate immediately on page load.
		const heroes = document.querySelectorAll( '[data-animate-hero]' );
		heroes.forEach( ( hero ) => {
			const targets = hero.querySelectorAll( '[data-animate]' );
			const sorted = Array.from( targets ).sort( ( a, b ) => {
				return (
					( parseFloat( a.getAttribute( 'data-animate-delay' ) ) ||
						0 ) -
					( parseFloat( b.getAttribute( 'data-animate-delay' ) ) ||
						0 )
				);
			} );
			sorted.forEach( ( el ) => {
				if ( isExcluded( el ) ) {
					return;
				}
				el.style.setProperty(
					'--alteia-anim-delay',
					resolveDelay( el ) + 's'
				);
				el.classList.add( 'is-animated' );
				el.addEventListener( 'transitionend', handleTransitionEnd, {
					once: true,
				} );
				const elementDelay =
					parseFloat( el.getAttribute( 'data-animate-delay' ) ) || 0;
				window.setTimeout(
					() => {
						el.classList.add( 'anim-done' );
					},
					durationMs + ( globalDelay + elementDelay ) * 1000 + 100
				);
			} );
			if ( hero.hasAttribute( 'data-animate' ) && ! isExcluded( hero ) ) {
				hero.style.setProperty(
					'--alteia-anim-delay',
					resolveDelay( hero ) + 's'
				);
				hero.classList.add( 'is-animated' );
				hero.addEventListener( 'transitionend', handleTransitionEnd, {
					once: true,
				} );
			}
		} );

		/**
		 * Observe a [data-animate] target with the shared Intersection Observer.
		 *
		 * @param {Element} el Candidate element.
		 */
		function observeTarget( el ) {
			if ( ! el || ! el.hasAttribute || ! el.hasAttribute( 'data-animate' ) ) {
				return;
			}

			if ( el.classList.contains( 'is-animated' ) ) {
				return;
			}

			if ( el.closest( '[data-animate-hero]' ) ) {
				return;
			}

			if ( isExcluded( el ) || isGroupedSequenceItem( el ) ) {
				return;
			}

			observer.observe( el );
		}

		/**
		 * Observe an added node and any [data-animate] descendants.
		 *
		 * @param {Node} node Added DOM node.
		 */
		function observeTree( node ) {
			if ( ! node || node.nodeType !== 1 ) {
				return;
			}

			observeTarget( node );

			if ( node.matches && node.matches( SEQUENCE_SELECTOR ) ) {
				observeWorkflowSequenceRoot( node );
			}

			if ( node.querySelectorAll ) {
				node.querySelectorAll( '[data-animate]' ).forEach( observeTarget );
				node.querySelectorAll( SEQUENCE_SELECTOR ).forEach(
					observeWorkflowSequenceRoot
				);
			}
		}

		// Observe every [data-animate] that is NOT inside a hero.
		document.querySelectorAll( '[data-animate]' ).forEach( observeTarget );

		document
			.querySelectorAll( SEQUENCE_SELECTOR )
			.forEach( observeWorkflowSequenceRoot );

		// Dynamically inserted markup (Post Listing filter / load more).
		if ( typeof MutationObserver !== 'undefined' && document.body ) {
			const mutationObserver = new MutationObserver( ( mutations ) => {
				mutations.forEach( ( mutation ) => {
					mutation.addedNodes.forEach( observeTree );
				} );
			} );

			mutationObserver.observe( document.body, {
				childList: true,
				subtree: true,
			} );
		}

		// Debounced resize: re-check with the same trigger rules (no pop-in).
		let resizeTimer = 0;
		window.addEventListener( 'resize', () => {
			window.clearTimeout( resizeTimer );
			resizeTimer = window.setTimeout( syncPendingTargets, 150 );
		} );

		// Back/forward cache can restore opacity:0 without re-running init.
		window.addEventListener( 'pageshow', ( event ) => {
			if ( event.persisted ) {
				syncPendingTargets();
			}
		} );
	}

	// Run on DOM ready.
	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}
} )();
